import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ErreurApi, appelApi, telechargeFichier, useRequete } from '../api/client';
import type {
  AnomalieCsv,
  Campagne,
  DiffCatalogue,
  EntreeJournal,
  Equipe as EquipeVue,
  Etiquette,
  Fonction,
  Grade,
  Personne,
  ResultatImportOrganisation,
  ResultatNotification,
  ResultatOuverture,
  Role,
  Technologie,
} from '../api/types';
import { useSession } from '../auth/session';
import {
  Anneau,
  Badge,
  Chargement,
  Message,
  dateCourte,
  dateLongue,
  jourCivil,
} from '../composants/elements';
import { ROLES, libelleRole } from '../composants/roles';

/**
 * Le back-office — §6.2. Campagnes, catalogue, personnes, exports, journal.
 * Chaque action passe par une route admin, donc chaque action est journalisée.
 */

type Onglet = 'campagnes' | 'catalogue' | 'equipes' | 'personnes' | 'journal';

const ONGLETS: { cle: Onglet; libelle: string }[] = [
  { cle: 'campagnes', libelle: 'Campaigns' },
  { cle: 'catalogue', libelle: 'Catalogue' },
  { cle: 'equipes', libelle: 'Teams' },
  { cle: 'personnes', libelle: 'People' },
  { cle: 'journal', libelle: 'Audit log' },
];

export function Administration(): JSX.Element {
  const [onglet, setOnglet] = useState<Onglet>('campagnes');

  return (
    <div className="contenu" style={{ paddingBottom: 'var(--space-9)' }}>
      <div className="entete-page">
        <div>
          <p className="eyebrow" style={{ marginBottom: 14 }}>
            Administration
          </p>
          <h1 style={{ fontSize: 'clamp(28px, 3.4vw, 44px)' }}>Back office</h1>
        </div>
      </div>

      <div className="onglets" role="tablist">
        {ONGLETS.map((o) => (
          <button
            key={o.cle}
            type="button"
            role="tab"
            aria-selected={onglet === o.cle}
            className={`onglet${onglet === o.cle ? ' onglet--actif' : ''}`}
            onClick={() => setOnglet(o.cle)}
          >
            {o.libelle}
          </button>
        ))}
      </div>

      <div style={{ paddingTop: 32 }}>
        {onglet === 'campagnes' ? <Campagnes /> : null}
        {onglet === 'catalogue' ? <Catalogue /> : null}
        {onglet === 'equipes' ? <Equipes /> : null}
        {onglet === 'personnes' ? <Personnes /> : null}
        {onglet === 'journal' ? <Journal /> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Campagnes
// ---------------------------------------------------------------------------

/** L'API renvoie l'état en français : la colonne, elle, se lit en anglais. */
const LIBELLES_ETAT_CAMPAGNE: Record<string, string> = {
  brouillon: 'Draft',
  ouverte: 'Open',
  fermee: 'Closed',
};

const MOMENTS = [
  { cle: 'ouverture', libelle: 'Campaign opened' },
  { cle: 'j7', libelle: 'One week left' },
  { cle: 'j1', libelle: 'Last day' },
  { cle: 'cloture', libelle: 'Campaign closed' },
  { cle: 'manuel', libelle: 'Plain reminder' },
] as const;

type CarteTeams = {
  attachments: {
    content: {
      body: { text?: string; facts?: { title: string; value: string }[] }[];
      actions?: { title: string; url: string }[];
    };
  }[];
};

function Campagnes(): JSX.Element {
  const session = useSession();
  const campagnes = useRequete<{ campagnes: Campagne[] }>('/api/campagnes');
  const [trimestre, setTrimestre] = useState('');
  const [edition, setEdition] = useState(trimestreCourant());
  const [debut, setDebut] = useState('');
  const [echeance, setEcheance] = useState('');
  const [teams, setTeams] = useState<{ trimestre: string; moment: string } | null>(null);
  const [modifiee, setModifiee] = useState<Campagne | null>(null);
  const [avis, setAvis] = useState<{ variante: 'succes' | 'erreur' | 'info'; texte: string } | null>(
    null,
  );

  async function agit(action: () => Promise<string>): Promise<void> {
    try {
      setAvis({ variante: 'info', texte: 'Working…' });
      const texte = await action();
      campagnes.recharge();
      await session.rafraichit();
      setAvis({ variante: 'succes', texte });
    } catch (cause) {
      setAvis({
        variante: 'erreur',
        texte: cause instanceof ErreurApi ? cause.message : 'Action failed',
      });
    }
  }

  return (
    <>
      {avis ? (
        <div style={{ marginBottom: 24 }}>
          <Message variante={avis.variante}>{avis.texte}</Message>
        </div>
      ) : null}

      <section style={{ paddingBottom: 44 }}>
        <h3 style={{ marginBottom: 16 }}>Create a quarter</h3>
        <form
          style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}
          onSubmit={(e) => {
            e.preventDefault();
            void agit(async () => {
              await appelApi('/api/admin/campagnes', {
                methode: 'POST',
                corps: {
                  trimestre,
                  editionRadar: edition || trimestre,
                  // 22:59 heure de Tunis : la journée entière reste ouverte.
                  debut: debut ? new Date(`${debut}T00:00:00`).toISOString() : null,
                  echeance: echeance ? new Date(`${echeance}T22:59:00`).toISOString() : null,
                },
              });
              setTrimestre('');
              setEdition('');
              setDebut('');
              setEcheance('');
              return `Campaign ${trimestre} created as a draft.`;
            });
          }}
        >
          <label className="champ" style={{ width: 150 }}>
            <span className="champ__libelle">Quarter</span>
            <input
              className="saisie"
              placeholder="2026-Q4"
              value={trimestre}
              onChange={(e) => setTrimestre(e.target.value)}
              required
            />
          </label>
          <label className="champ" style={{ width: 170 }}>
            <span className="champ__libelle">Radar edition</span>
            <input
              className="saisie"
              placeholder="same as quarter"
              value={edition}
              onChange={(e) => setEdition(e.target.value)}
            />
          </label>
          <label className="champ" style={{ width: 170 }}>
            <span className="champ__libelle">Starts</span>
            <input
              className="saisie"
              type="date"
              value={debut}
              onChange={(e) => setDebut(e.target.value)}
            />
          </label>
          <label className="champ" style={{ width: 170 }}>
            <span className="champ__libelle">Ends</span>
            <input
              className="saisie"
              type="date"
              value={echeance}
              onChange={(e) => setEcheance(e.target.value)}
            />
          </label>
          <button type="submit" className="bouton bouton--primaire">
            Create
          </button>
        </form>
        <p className="discret" style={{ marginTop: 12 }}>
          Opening a campaign carries over last quarter’s levels. The operation is idempotent:
          opening twice duplicates nothing.
        </p>
      </section>

      {campagnes.enCours && !campagnes.donnees ? <Chargement /> : null}

      <div className="tableau-defilant">
        <table className="tableau">
          <thead>
            <tr>
              <th>Quarter</th>
              <th>Edition</th>
              <th>State</th>
              <th>Entry window</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(campagnes.donnees?.campagnes ?? []).map((c) => (
              <tr key={c.trimestre}>
                <td style={{ fontFamily: 'var(--font-title)', fontWeight: 600 }}>{c.trimestre}</td>
                <td className="mono">{c.editionRadar}</td>
                <td>
                  <Badge variante={c.etat === 'ouverte' ? 'nouveau' : undefined}>
                    {LIBELLES_ETAT_CAMPAGNE[c.etat] ?? c.etat}
                  </Badge>
                </td>
                {/* Une seule colonne : la fenêtre de saisie. Les dates réelles
                    d'ouverture et de clôture sont dans le journal d'audit, pas
                    dans un tableau qu'on lit tous les jours. */}
                <td className="discret">
                  {c.debut || c.echeance ? (
                    <>
                      {c.debut ? dateCourte(c.debut) : '—'} → {c.echeance ? dateCourte(c.echeance) : '—'}
                    </>
                  ) : (
                    'not set'
                  )}
                </td>
                <td>
                  <div className="barre-actions">
                    <button
                      type="button"
                      className="bouton bouton--petit"
                      disabled={c.etat === 'fermee'}
                      title={
                        c.etat === 'fermee'
                          ? 'A closed campaign can no longer be changed'
                          : undefined
                      }
                      onClick={() => setModifiee(c)}
                    >
                      Edit…
                    </button>

                    {c.etat === 'brouillon' ? (
                      <button
                        type="button"
                        className="bouton bouton--petit"
                        onClick={() =>
                          void agit(async () => {
                            const r = await appelApi<ResultatOuverture>(
                              `/api/admin/campagnes/${c.trimestre}/ouvrir`,
                              { methode: 'POST' },
                            );
                            return `${c.trimestre} opened: ${r.evaluationsCreees} profile(s), ${r.notesReportees} level(s) carried over for review.`;
                          })
                        }
                      >
                        Open
                      </button>
                    ) : null}

                    {c.etat === 'ouverte' ? (
                      <button
                        type="button"
                        className="bouton bouton--petit"
                        onClick={() =>
                          void agit(async () => {
                            await appelApi(`/api/admin/campagnes/${c.trimestre}/fermer`, {
                              methode: 'POST',
                            });
                            return `${c.trimestre} closed. Profiles are now read-only, for good.`;
                          })
                        }
                      >
                        Close
                      </button>
                    ) : null}

                    <button
                      type="button"
                      className="bouton bouton--petit"
                      onClick={() => setTeams({ trimestre: c.trimestre, moment: 'manuel' })}
                    >
                      Post to Teams…
                    </button>

                    <button
                      type="button"
                      className="bouton bouton--petit"
                      onClick={() =>
                        void telechargeFichier(
                          `/api/admin/campagnes/${c.trimestre}/export.csv`,
                          `competences-elyadata-${c.trimestre}.csv`,
                        ).catch((cause: unknown) =>
                          setAvis({
                            variante: 'erreur',
                            texte: cause instanceof Error ? cause.message : 'Export failed',
                          }),
                        )
                      }
                    >
                      Export CSV
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modifiee ? (
        <FormulaireCampagne
          campagne={modifiee}
          fermer={() => setModifiee(null)}
          agit={agit}
        />
      ) : null}

      {teams ? (
        <ApercuTeams
          trimestre={teams.trimestre}
          moment={teams.moment}
          setMoment={(moment) => setTeams({ ...teams, moment })}
          fermer={() => setTeams(null)}
          agit={agit}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------

function LigneDiffListe({
  titre,
  lignes,
}: {
  titre: string;
  lignes: { nom: string; titre: string; radar: string; anneau: string }[];
}): JSX.Element | null {
  if (lignes.length === 0) return null;
  return (
    <div className="carte">
      <h4>
        {titre} · {lignes.length}
      </h4>
      <p className="discret">
        {lignes
          .slice(0, 12)
          .map((l) => l.titre)
          .join(', ')}
        {lignes.length > 12 ? `, and ${lignes.length - 12} more` : ''}
      </p>
    </div>
  );
}

/** Un `rd.json` déposé à la main, déjà analysé. */
type FichierRadar = { radar: string; nomFichier: string; contenu: unknown; entrees: number };

/** `2026-Q3` pour le 27 juillet 2026 — la valeur par défaut de l'édition. */
function trimestreCourant(): string {
  const maintenant = new Date();
  return `${maintenant.getFullYear()}-Q${Math.floor(maintenant.getMonth() / 3) + 1}`;
}

function Catalogue(): JSX.Element {
  const catalogue = useRequete<{ technologies: Technologie[] }>('/api/admin/catalogue?inclureInactives=true');
  // Préremplie : le geste courant est d'importer l'édition du trimestre en cours.
  const [edition, setEdition] = useState(trimestreCourant());
  const [diff, setDiff] = useState<DiffCatalogue | null>(null);
  const [avis, setAvis] = useState<{ variante: 'succes' | 'erreur' | 'info'; texte: string } | null>(
    null,
  );
  const [radar, setRadar] = useState('');
  const [fichiers, setFichiers] = useState<FichierRadar[]>([]);
  const [survol, setSurvol] = useState(false);

  /**
   * Le nom du radar vient du chemin du fichier quand le navigateur le donne
   * (`webkitRelativePath` sur un dossier déposé : `data/tech/rd.json`), sinon du
   * nom du fichier (`tech.json`, `rd-tech.json`). Faute de quoi il reste à saisir.
   */
  function radarDevine(fichier: File): string {
    const chemin = (fichier as File & { webkitRelativePath?: string }).webkitRelativePath ?? '';
    const segments = chemin.split('/').filter(Boolean);
    if (segments.length >= 2) return segments[segments.length - 2]!;
    const base = fichier.name.replace(/\.json$/i, '').replace(/^rd[-_]?/i, '');
    return base === 'rd' ? '' : base;
  }

  async function ajouteFichiers(liste: FileList | null): Promise<void> {
    if (!liste) return;
    const lus: FichierRadar[] = [];

    for (const fichier of Array.from(liste)) {
      try {
        const contenu: unknown = JSON.parse(await fichier.text());
        const entrees = Array.isArray(contenu)
          ? contenu.length
          : Array.isArray((contenu as { items?: unknown[] }).items)
            ? (contenu as { items: unknown[] }).items.length
            : Array.isArray((contenu as { blips?: unknown[] }).blips)
              ? (contenu as { blips: unknown[] }).blips.length
              : 0;
        lus.push({ radar: radarDevine(fichier), nomFichier: fichier.name, contenu, entrees });
      } catch {
        setAvis({ variante: 'erreur', texte: `${fichier.name} is not valid JSON.` });
        return;
      }
    }

    setDiff(null);
    setFichiers((actuels) => [...actuels, ...lus]);
    setAvis(null);
  }

  async function importe(appliquer: boolean): Promise<void> {
    try {
      setAvis({
        variante: 'info',
        texte: appliquer ? 'Writing…' : fichiers.length > 0 ? 'Reading the files…' : 'Reading the radar…',
      });
      const r = await appelApi<{ diff: DiffCatalogue; applique: boolean }>(
        '/api/admin/catalogue/importer',
        {
          methode: 'POST',
          corps: {
            edition,
            appliquer,
            ...(fichiers.length > 0
              ? { fichiers: fichiers.map((f) => ({ radar: f.radar, contenu: f.contenu })) }
              : {}),
          },
        },
      );
      setDiff(r.diff);
      if (appliquer) {
        catalogue.recharge();
        setAvis({ variante: 'succes', texte: `Edition ${edition} applied.` });
      } else {
        setAvis({
          variante: r.diff.doublonsProbables.length > 0 ? 'erreur' : 'info',
          texte:
            r.diff.doublonsProbables.length > 0
              ? 'Careful: a key rename looks likely, and history would be lost. Check before applying.'
              : 'Diff computed. Nothing was written.',
        });
      }
    } catch (cause) {
      setAvis({
        variante: 'erreur',
        texte: cause instanceof ErreurApi ? cause.message : 'Import failed',
      });
    }
  }

  /**
   * Ce qui empêche chaque bouton d'agir, en une phrase. Calculé ici plutôt
   * qu'inféré du bouton grisé.
   */
  const sansNomDeRadar = fichiers.filter((f) => f.radar.trim().length === 0);
  const obstacleDiff =
    edition.trim().length === 0
      ? 'Name the edition first — a campaign points at one, so it needs a name like 2026-Q4.'
      : sansNomDeRadar.length > 0
        ? `Give a radar name to ${sansNomDeRadar.map((f) => f.nomFichier).join(', ')} — it could not be guessed from the file name.`
        : null;

  const obstacleApply =
    obstacleDiff ??
    (diff === null ? 'Compute the diff first: nothing is written before you have seen it.' : null);

  const technologies = (catalogue.donnees?.technologies ?? []).filter(
    (t) => !radar || t.radar === radar,
  );
  const radars = [...new Set((catalogue.donnees?.technologies ?? []).map((t) => t.radar))];

  return (
    <>
      <section style={{ paddingBottom: 40 }}>
        <h3 style={{ marginBottom: 10 }}>Import a radar edition</h3>
        <p className="discret" style={{ marginBottom: 28, maxWidth: 620 }}>
          Nothing is written until you approve the diff. A removed technology is deactivated, never
          deleted — past quarters stay readable.
        </p>

        <div className="etapes">
          <div className="etape">
            <span className="etape__numero">01</span>
            <div className="etape__corps">
              <h4>Source</h4>
              <p className="discret">
                {fichiers.length === 0
                  ? 'The published radar, unless you drop files below.'
                  : `${fichiers.length} file(s) — used instead of the published radar.`}
              </p>
            </div>
          </div>
          <div className="etape">
            <span className="etape__numero">02</span>
            <div className="etape__corps">
              <h4>Edition</h4>
              <p className="discret">The label a campaign will point at, like 2026-Q3.</p>
            </div>
          </div>
          <div className="etape">
            <span className="etape__numero">03</span>
            <div className="etape__corps">
              <h4>Review, then apply</h4>
              <p className="discret">Added, removed, moved, renamed — and the warnings.</p>
            </div>
          </div>
        </div>

        <div
          className={`depot${survol ? ' depot--survol' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setSurvol(true);
          }}
          onDragLeave={() => setSurvol(false)}
          onDrop={(e) => {
            e.preventDefault();
            setSurvol(false);
            void ajouteFichiers(e.dataTransfer.files);
          }}
        >
          <div className="depot__invite">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M12 16V4" />
              <path d="m7 9 5-5 5 5" />
              <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
            </svg>
            <p>
              Drop <code>rd.json</code> files here
            </p>
            <label className="bouton bouton--petit" style={{ cursor: 'pointer' }}>
              or choose files
              <input
                type="file"
                accept="application/json,.json"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  void ajouteFichiers(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
            <p className="discret">
              One file per radar. Leave empty to pull from the published radar.
            </p>
          </div>

          {fichiers.length > 0 ? (
            <table className="tableau" style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th>File</th>
                  <th>Entries</th>
                  <th>Radar name</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {fichiers.map((f, index) => (
                  <tr key={`${f.nomFichier}-${index}`}>
                    <td className="mono">{f.nomFichier}</td>
                    <td className="mono discret">{f.entrees || '?'}</td>
                    <td>
                      <input
                        className="saisie"
                        style={{ maxWidth: 160 }}
                        placeholder="tech"
                        value={f.radar}
                        onChange={(e) =>
                          setFichiers((actuels) =>
                            actuels.map((autre, i) =>
                              i === index ? { ...autre, radar: e.target.value } : autre,
                            ),
                          )
                        }
                        aria-label={`Radar name for ${f.nomFichier}`}
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className="bouton bouton--fantome bouton--petit"
                        onClick={() => {
                          setFichiers((actuels) => actuels.filter((_, i) => i !== index));
                          setDiff(null);
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>

        {/* La barre d'édition : le nom qu'on donne à l'import, et les deux gestes
            qui en découlent. L'indice vit dedans, sous les boutons qu'il explique. */}
        <div className="barre-import">
          <div className="barre-import__haut">
            <label className="champ barre-import__champ">
              <span className="champ__libelle">Edition</span>
              <input
                className="saisie saisie--mono"
                placeholder="2026-Q4"
                value={edition}
                onChange={(e) => setEdition(e.target.value)}
                aria-label="Edition"
                spellCheck={false}
                autoComplete="off"
              />
              <span className="champ__aide">Year and quarter, like 2026-Q4.</span>
            </label>

            <div className="barre-import__actions">
              <button
                type="button"
                className="bouton"
                disabled={obstacleDiff !== null}
                title={obstacleDiff ?? undefined}
                onClick={() => void importe(false)}
              >
                {diff === null ? 'Compute the diff' : 'Recompute the diff'}
              </button>
              <button
                type="button"
                className="bouton bouton--primaire"
                disabled={obstacleApply !== null}
                title={obstacleApply ?? undefined}
                onClick={() => void importe(true)}
              >
                Apply
              </button>
            </div>
          </div>

          {/* Un bouton désactivé qui ne dit pas ce qu'il attend n'aide personne. */}
          {(obstacleDiff ?? obstacleApply) ? (
            <p className="barre-import__indice">{obstacleDiff ?? obstacleApply}</p>
          ) : null}
        </div>

        {avis ? (
          <div style={{ marginTop: 20 }}>
            <Message variante={avis.variante}>{avis.texte}</Message>
          </div>
        ) : null}

        {diff ? (
          <div style={{ marginTop: 24 }}>
            <p className="discret" style={{ marginBottom: 16 }}>
              Source {diff.source} · {diff.total} technolog{diff.total === 1 ? 'y' : 'ies'} read,{' '}
              {diff.inchangees} unchanged. Scope: {diff.radars.join(', ')} — other radars are left
              untouched.
            </p>

            {diff.doublonsProbables.length > 0 ? (
              <div style={{ marginBottom: 20 }}>
                <Message variante="erreur">
                  <strong>Likely key rename.</strong>
                  <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                    {diff.doublonsProbables.map((d) => (
                      <li key={d.retiree.nom}>{d.raison}</li>
                    ))}
                  </ul>
                </Message>
              </div>
            ) : null}

            {diff.problemesSource.length > 0 ? (
              <div style={{ marginBottom: 20 }}>
                <Message variante="attention">
                  {diff.problemesSource.length} entry(ies) skipped while reading:{' '}
                  {diff.problemesSource.slice(0, 3).join(' ; ')}
                  {diff.problemesSource.length > 3 ? '…' : ''}
                </Message>
              </div>
            ) : null}

            <div className="grille-cartes">
              <LigneDiffListe titre="Added" lignes={diff.ajoutees} />
              <LigneDiffListe titre="Reactivated" lignes={diff.reactivees} />
              <LigneDiffListe titre="Removed" lignes={diff.retirees} />
              <LigneDiffListe titre="Moved" lignes={diff.deplacees} />
              <LigneDiffListe titre="Renamed" lignes={diff.renommees} />
            </div>
          </div>
        ) : null}
      </section>

      <section>
        <div className="entete-groupe">
          <h3>Catalogue ({technologies.length})</h3>
          <select
            className="selection"
            style={{ width: 'auto' }}
            value={radar}
            onChange={(e) => setRadar(e.target.value)}
            aria-label="Filtrer par radar"
          >
            <option value="">Tous les radars</option>
            {radars.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        <div className="tableau-defilant">
          <table className="tableau">
            <thead>
              <tr>
                <th>Technology</th>
                <th>Radar</th>
                <th>Group</th>
                <th>Ring</th>
                <th>Seen / first seen</th>
                <th>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {technologies.map((t) => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 500 }}>{t.titre}</td>
                  <td className="discret">{t.radar}</td>
                  <td className="discret">{t.quadrant}</td>
                  <td>
                    <Anneau anneau={t.anneau} />
                    {t.anneauPrecedent ? (
                      <span className="discret"> ← {t.anneauPrecedent}</span>
                    ) : null}
                  </td>
                  <td className="mono discret">
                    {t.vueAEdition} / {t.apparueAEdition}
                  </td>
                  <td>{t.actif ? <Badge variante="ok">active</Badge> : <Badge>disabled</Badge>}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="bouton bouton--fantome bouton--petit"
                      onClick={() => {
                        void appelApi(`/api/admin/catalogue/${t.id}`, {
                          methode: 'PATCH',
                          corps: { actif: !t.actif },
                        }).then(() => catalogue.recharge());
                      }}
                    >
                      {t.actif ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/**
 * Modifier une campagne : trimestre, édition, début, fin — en une fois, validé
 * à l'enregistrement.
 *
 * Les dates étaient éditables à même le tableau. C'était trop léger : un clic de
 * travers déplaçait une échéance qui porte le compte à rebours de toute la
 * maison et les cartes Teams. Et le trimestre comme l'édition n'étaient pas
 * corrigeables du tout — une faute de frappe à la création restait.
 */
function FormulaireCampagne({
  campagne,
  fermer,
  agit,
}: {
  campagne: Campagne;
  fermer: () => void;
  agit: (action: () => Promise<string>) => Promise<void>;
}): JSX.Element {
  const jour = jourCivil;

  const [trimestre, setTrimestre] = useState(campagne.trimestre);
  const [edition, setEdition] = useState(campagne.editionRadar);
  const [debut, setDebut] = useState(jour(campagne.debut));
  const [fin, setFin] = useState(jour(campagne.echeance));

  const desordre = debut !== '' && fin !== '' && fin <= debut;
  const inchange =
    trimestre === campagne.trimestre &&
    edition === campagne.editionRadar &&
    debut === jour(campagne.debut) &&
    fin === jour(campagne.echeance);

  return (
    <section className="apercu-teams">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <p className="eyebrow" style={{ marginBottom: 8 }}>
            Campaign · {campagne.trimestre}
          </p>
          <h3>Edit the campaign.</h3>
        </div>
        <button type="button" className="bouton bouton--fantome bouton--petit" onClick={fermer}>
          Cancel
        </button>
      </div>

      <form
        style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', marginTop: 20 }}
        onSubmit={(e) => {
          e.preventDefault();
          void agit(async () => {
            await appelApi(`/api/admin/campagnes/${campagne.trimestre}`, {
              methode: 'PATCH',
              corps: {
                trimestre,
                editionRadar: edition,
                debut: debut ? new Date(`${debut}T00:00:00`).toISOString() : null,
                // 22:59 : la journée de fin reste entière.
                echeance: fin ? new Date(`${fin}T22:59:00`).toISOString() : null,
              },
            });
            fermer();
            return `${trimestre} updated.`;
          });
        }}
      >
        <label className="champ" style={{ width: 150 }}>
          <span className="champ__libelle">Quarter</span>
          <input
            className="saisie"
            value={trimestre}
            onChange={(e) => setTrimestre(e.target.value)}
            placeholder="2026-Q3"
            required
          />
        </label>

        <label className="champ" style={{ width: 170 }}>
          <span className="champ__libelle">Radar edition</span>
          <input
            className="saisie"
            value={edition}
            onChange={(e) => setEdition(e.target.value)}
            required
          />
        </label>

        <label className="champ" style={{ width: 170 }}>
          <span className="champ__libelle">Starts</span>
          <input
            className="saisie"
            type="date"
            value={debut}
            onChange={(e) => setDebut(e.target.value)}
          />
        </label>

        <label className="champ" style={{ width: 170 }}>
          <span className="champ__libelle">Ends</span>
          <input
            className={`saisie${desordre ? ' zone-texte--incomplete' : ''}`}
            type="date"
            value={fin}
            onChange={(e) => setFin(e.target.value)}
          />
        </label>

        <button
          type="submit"
          className="bouton bouton--primaire"
          disabled={desordre || inchange || trimestre.trim().length === 0}
        >
          Save
        </button>
      </form>

      {desordre ? (
        <p className="discret" style={{ marginTop: 12, color: 'var(--warning)' }}>
          The end date must come after the start date.
        </p>
      ) : null}

      <p className="discret" style={{ marginTop: 16 }}>
        Opened {campagne.ouverteLe ? dateCourte(campagne.ouverteLe) : 'not yet'}
        {campagne.fermeeLe ? `, closed ${dateCourte(campagne.fermeeLe)}` : ''}. Renaming the quarter
        changes what the campaign is called everywhere; the ratings stay attached to it.
      </p>
    </section>
  );
}

/**
 * Relire avant de poster. Un canal Teams est public et une carte ne se retire
 * pas : choisir le moment à l'aveugle fait annoncer « plus qu'une semaine » à un
 * mois de l'échéance.
 */
function ApercuTeams({
  trimestre,
  moment,
  setMoment,
  fermer,
  agit,
}: {
  trimestre: string;
  moment: string;
  setMoment: (moment: string) => void;
  fermer: () => void;
  agit: (action: () => Promise<string>) => Promise<void>;
}): JSX.Element {
  const [carte, setCarte] = useState<CarteTeams | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let annule = false;
    setCarte(null);
    setErreur(null);

    appelApi<{ carte: CarteTeams }>(`/api/admin/campagnes/${trimestre}/notifier`, {
      methode: 'POST',
      corps: { moment, apercu: true },
    })
      .then((r) => {
        if (!annule) setCarte(r.carte);
      })
      .catch((cause: unknown) => {
        if (!annule) setErreur(cause instanceof Error ? cause.message : 'Preview failed');
      });

    return () => {
      annule = true;
    };
  }, [trimestre, moment]);

  const contenu = carte?.attachments[0]?.content;
  const textes = contenu?.body.filter((b) => b.text) ?? [];
  const faits = contenu?.body.find((b) => b.facts)?.facts ?? [];

  return (
    <section className="apercu-teams">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <p className="eyebrow" style={{ marginBottom: 8 }}>
            Teams card · {trimestre}
          </p>
          <h3>Read it before it lands in the channel.</h3>
        </div>
        <button type="button" className="bouton bouton--fantome bouton--petit" onClick={fermer}>
          Close
        </button>
      </div>

      <div className="barre-actions" style={{ paddingBlock: 16 }}>
        {MOMENTS.map((m) => (
          <button
            key={m.cle}
            type="button"
            className={`puce${moment === m.cle ? ' puce--active' : ''}`}
            aria-pressed={moment === m.cle}
            onClick={() => setMoment(m.cle)}
          >
            {m.libelle}
          </button>
        ))}
      </div>

      {erreur ? <Message variante="erreur">{erreur}</Message> : null}

      {carte === null && !erreur ? <Chargement quoi="Building the card" /> : null}

      {carte ? (
        <div className="carte-teams">
          <p className="carte-teams__titre">{textes[0]?.text}</p>
          <p className="discret" style={{ marginTop: 6 }}>
            {textes[1]?.text}
          </p>
          <table className="tableau" style={{ marginTop: 14 }}>
            <tbody>
              {faits.map((f) => (
                <tr key={f.title}>
                  <td className="discret" style={{ width: 170 }}>
                    {f.title}
                  </td>
                  <td>{f.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {contenu?.actions?.[0] ? (
            <p className="discret" style={{ marginTop: 12 }}>
              Button “{contenu.actions[0].title}” → {contenu.actions[0].url}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="barre-actions" style={{ marginTop: 18 }}>
        <button
          type="button"
          className="bouton bouton--rose"
          disabled={carte === null}
          onClick={() =>
            void agit(async () => {
              const r = await appelApi<ResultatNotification>(
                `/api/admin/campagnes/${trimestre}/notifier`,
                { methode: 'POST', corps: { moment } },
              );
              fermer();
              return r.envoyee
                ? `Card posted to the Teams channel for ${trimestre}.`
                : `Card not sent: ${r.raison}`;
            })
          }
        >
          Post this card
        </button>
        <span className="discret">A posted card cannot be withdrawn.</span>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Équipes
// ---------------------------------------------------------------------------

type ReponseEquipes = {
  equipes: EquipeVue[];
  natures: Etiquette[];
  raisonEtreMax: number;
};

function Equipes(): JSX.Element {
  const equipes = useRequete<ReponseEquipes>('/api/admin/equipes');

  return (
    <>
      <p className="discret" style={{ maxWidth: 640, marginBottom: 20 }}>
        A team has one nature among five: chapter, project, guild, internal group — or location, a
        nature of its own. Say what it is for: a name alone does not tell what “Data” does. Where a
        team is delivered from is set on its own page, by scoping it to one or more locations.
        Someone can belong to several teams at once, with a different function in each.
      </p>

      <div className="entete-groupe" style={{ marginBottom: 20 }}>
        <h3>Teams ({(equipes.donnees?.equipes ?? []).length})</h3>
        <Link to="/administration/equipes/new" className="bouton bouton--primaire">
          New team
        </Link>
      </div>

      <div className="tableau-defilant">
        <table className="tableau">
          <thead>
            <tr>
              <th>Team</th>
              <th>Nature</th>
              <th>Locations</th>
              <th className="tableau__nombre">Members</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {(equipes.donnees?.equipes ?? []).map((e) => (
              <tr key={e.id}>
                <td>
                  <Link to={`/administration/equipes/${e.id}`}>{e.nom}</Link>
                </td>
                <td>{e.libelleNature ?? <span className="discret">Not set</span>}</td>
                <td>
                  {e.lieux.length === 0 ? (
                    <span className="discret">—</span>
                  ) : (
                    e.lieux.map((l) => l.nom).join(', ')
                  )}
                </td>
                <td className="tableau__nombre">{e.membres}</td>
                <td>{e.actif ? <Badge variante="ok">active</Badge> : <Badge>archived</Badge>}</td>
              </tr>
            ))}
            {(equipes.donnees?.equipes ?? []).length === 0 ? (
              <tr>
                <td colSpan={5} className="discret">
                  No team yet. Create a chapter to get started.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Personnes
// ---------------------------------------------------------------------------

type ReponsePersonnes = {
  personnes: (Personne & {
    appartenances: { equipeId: string; fonction: string | null; libelleFonction: string }[];
  })[];
  equipes: EquipeVue[];
  /** Vocabulaire des fonctions par nature — ce que la barre d'affectation propose. */
  fonctions: Record<string, Fonction[]>;
  /**
   * L'échelle des grades, avec ses libellés. Elle vient de l'API
   * (`api/src/domain/grades.ts`) et n'est plus recopiée côté client : une règle
   * du domaine que l'API peut porter ne se recopie pas.
   */
  grades: Grade[];
  /** Le libellé du grade absent — « Not set », dit par le domaine. */
  gradeNonRenseigne: string;
};

function Personnes(): JSX.Element {
  const personnes = useRequete<ReponsePersonnes>('/api/admin/personnes');
  const [erreur, setErreur] = useState<string | undefined>(undefined);
  const [avis, setAvis] = useState<{ variante: 'succes' | 'erreur'; texte: string } | null>(null);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [equipeCible, setEquipeCible] = useState('');
  const [fonctionCible, setFonctionCible] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [modalImport, setModalImport] = useState(false);
  const boutonImport = useRef<HTMLButtonElement>(null);
  const modalEtaitOuvert = useRef(false);

  // Même geste qu'à la Task 9 pour `ModalNouvelleEquipe` : le focus revient au
  // bouton qui a ouvert le modal, quel que soit le chemin de fermeture.
  useEffect(() => {
    if (modalEtaitOuvert.current && !modalImport) {
      boutonImport.current?.focus();
    }
    modalEtaitOuvert.current = modalImport;
  }, [modalImport]);

  const equipesActives = (personnes.donnees?.equipes ?? []).filter((e) => e.actif);
  const equipeChoisie = equipesActives.find((e) => e.id === equipeCible);
  const fonctionsProposees = (personnes.donnees?.fonctions ?? {})[equipeChoisie?.nature ?? ''] ?? [];

  // L'échelle vient de la même réponse que les personnes : les replis ci-dessous
  // ne sont jamais observés, puisqu'aucune ligne de tableau n'existe avant que
  // `donnees` soit là. C'est ce qui dispense de recopier l'échelle localement
  // « en attendant la réponse ».
  const grades = personnes.donnees?.grades ?? [];
  const gradeNonRenseigne = personnes.donnees?.gradeNonRenseigne ?? '';

  async function modifie(id: string, corps: Record<string, unknown>): Promise<void> {
    try {
      await appelApi(`/api/admin/personnes/${id}`, { methode: 'PATCH', corps });
      personnes.recharge();
      setErreur(undefined);
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Update failed');
    }
  }

  const listePersonnes = personnes.donnees?.personnes ?? [];

  /** L'adresse du responsable, telle qu'on la saisit — jamais son identifiant. */
  function emailDe(responsableId: string | null | undefined): string {
    if (!responsableId) return '';
    return listePersonnes.find((x) => x.id === responsableId)?.email ?? '';
  }

  /**
   * Résout l'adresse saisie en identifiant, insensiblement à la casse. Vide :
   * on retire le responsable. Inconnue : on n'appelle pas — une adresse
   * inconnue n'est pas un compte à créer, une personne n'existe qu'après sa
   * première connexion (même règle que l'import de l'organigramme).
   */
  async function definitResponsable(
    personne: { id: string; responsableId?: string | null },
    saisie: string,
  ): Promise<void> {
    const propre = saisie.trim();

    if (propre === '') {
      if (personne.responsableId) await modifie(personne.id, { responsableId: null });
      return;
    }

    const trouve = listePersonnes.find((x) => x.email.toLowerCase() === propre.toLowerCase());
    if (!trouve) {
      setErreur(`No person found for “${propre}” — someone only exists after their first sign-in.`);
      return;
    }
    if (trouve.id === personne.responsableId) return;
    await modifie(personne.id, { responsableId: trouve.id });
  }

  function bascule(id: string): void {
    setSelection((actuelle) => {
      const suivante = new Set(actuelle);
      if (suivante.has(id)) suivante.delete(id);
      else suivante.add(id);
      return suivante;
    });
  }

  async function affecteSelection(): Promise<void> {
    if (selection.size === 0 || !equipeCible || enCours) return;
    setEnCours(true);
    try {
      const r = await appelApi<{ touchees: number }>('/api/admin/affectations', {
        methode: 'POST',
        corps: {
          personneIds: [...selection],
          equipeId: equipeCible,
          fonction: fonctionCible || null,
        },
      });
      setSelection(new Set());
      setEquipeCible('');
      setFonctionCible('');
      personnes.recharge();
      // `touchees` compte ce qui a réellement changé, pas la sélection : une
      // personne déjà dans le groupe avec la même fonction n'est pas comptée.
      setAvis({
        variante: 'succes',
        texte:
          r.touchees > 0
            ? `${r.touchees} ${r.touchees === 1 ? 'membership' : 'memberships'} updated for “${equipeChoisie?.nom ?? ''}”.`
            : `No change: the selection already matches “${equipeChoisie?.nom ?? ''}”.`,
      });
    } catch (cause) {
      setAvis({
        variante: 'erreur',
        texte: cause instanceof ErreurApi ? cause.message : 'Assignment failed',
      });
    } finally {
      setEnCours(false);
    }
  }

  return (
    <>
      <p className="discret" style={{ maxWidth: 640, marginBottom: 20 }}>
        Management roles live in the database: you can promote someone without going through IT.
        The admin role comes from the Entra security group. A deactivated person drops out of future
        campaigns and stays in the history.
      </p>

      {erreur ? (
        <div style={{ marginBottom: 20 }}>
          <Message variante="erreur">{erreur}</Message>
        </div>
      ) : null}

      {avis ? (
        <div style={{ marginBottom: 20 }}>
          <Message variante={avis.variante}>{avis.texte}</Message>
        </div>
      ) : null}

      <div className="entete-groupe" style={{ marginBottom: 20 }}>
        <h3>People ({(personnes.donnees?.personnes ?? []).length})</h3>
        <button
          ref={boutonImport}
          type="button"
          className="bouton"
          onClick={() => setModalImport(true)}
        >
          Import org chart…
        </button>
      </div>

      {selection.size > 0 ? (
        <div className="barre-import" style={{ marginBottom: 20 }}>
          <div className="barre-import__haut">
            <p style={{ margin: 0, fontWeight: 600 }}>
              Assign {selection.size} {selection.size === 1 ? 'person' : 'people'} to…
            </p>
            <div className="barre-import__actions">
              <select
                className="selection"
                style={{ width: 'auto' }}
                value={equipeCible}
                onChange={(e) => {
                  setEquipeCible(e.target.value);
                  setFonctionCible('');
                }}
                aria-label="Team to assign to"
              >
                <option value="">Choose a team…</option>
                {equipesActives.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nom}
                  </option>
                ))}
              </select>
              <select
                className="selection"
                style={{ width: 'auto' }}
                value={fonctionCible}
                onChange={(e) => setFonctionCible(e.target.value)}
                aria-label="Function to assign"
                disabled={equipeCible === ''}
              >
                <option value="">Member</option>
                {fonctionsProposees.map((f) => (
                  <option key={f.cle} value={f.cle}>
                    {f.libelle}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="bouton bouton--primaire"
                disabled={equipeCible === '' || enCours}
                onClick={() => void affecteSelection()}
              >
                Assign
              </button>
              <button
                type="button"
                className="bouton bouton--fantome"
                onClick={() => setSelection(new Set())}
              >
                Clear selection
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="tableau-defilant">
        <table className="tableau">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  aria-label="Select all people"
                  checked={
                    (personnes.donnees?.personnes ?? []).length > 0 &&
                    selection.size === (personnes.donnees?.personnes ?? []).length
                  }
                  onChange={(e) =>
                    setSelection(
                      e.target.checked
                        ? new Set((personnes.donnees?.personnes ?? []).map((p) => p.id))
                        : new Set(),
                    )
                  }
                />
              </th>
              <th>Person</th>
              <th>Teams</th>
              <th>Job title</th>
              <th>Grade</th>
              <th>Manager</th>
              <th>Function</th>
              <th>Access</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            {(personnes.donnees?.personnes ?? []).map((p) => (
              <tr key={p.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selection.has(p.id)}
                    onChange={() => bascule(p.id)}
                    aria-label={`Select ${p.nom}`}
                  />
                </td>
                <td>
                  {p.nom}
                  <br />
                  <span className="discret">{p.email}</span>
                </td>
                <td>
                  {/* Plusieurs appartenances : on coche, on ne saisit pas. */}
                  <span className="barre-actions">
                    {(personnes.donnees?.equipes ?? [])
                      .filter((e) => e.actif)
                      .map((e) => {
                        const membre = p.appartenances.some((a) => a.equipeId === e.id);
                        return (
                          <button
                            key={e.id}
                            type="button"
                            className={`puce${membre ? ' puce--active' : ''}`}
                            aria-pressed={membre}
                            title={e.etiquettes.map((t) => t.libelle).join(', ')}
                            onClick={() =>
                              // La fonction déjà tenue dans les autres groupes est préservée ;
                              // un groupe qu'on vient de cocher part sans fonction (Member).
                              void modifie(p.id, {
                                appartenances: membre
                                  ? p.appartenances.filter((a) => a.equipeId !== e.id)
                                  : [...p.appartenances, { equipeId: e.id, fonction: null }],
                              })
                            }
                          >
                            {e.nom}
                          </button>
                        );
                      })}
                  </span>
                </td>
                <td>
                  {/* Entra fournirait l'intitulé via le claim `jobTitle`, mais il
                      n'est pas toujours configuré : on l'écrit ici en attendant. */}
                  <input
                    className="saisie"
                    list="intitules"
                    defaultValue={p.intitulePoste ?? ''}
                    placeholder="Job title"
                    onBlur={(e) => {
                      const valeur = e.target.value.trim();
                      if (valeur !== (p.intitulePoste ?? '')) {
                        void modifie(p.id, { intitulePoste: valeur || null });
                      }
                    }}
                    aria-label={`Job title of ${p.nom}`}
                  />
                </td>
                <td>
                  <select
                    className="selection"
                    style={{ width: 'auto' }}
                    value={p.grade ?? ''}
                    onChange={(e) =>
                      void modifie(p.id, {
                        grade: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                    aria-label={`Grade of ${p.nom}`}
                  >
                    <option value="">{gradeNonRenseigne}</option>
                    {grades.map((g) => (
                      <option key={g.grade} value={g.grade}>
                        {g.libelle}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {/* La ligne hiérarchique (§3.3, jalon 4 de la spec
                      lieux-et-hiérarchie), à côté du grade parce que c'est lui
                      qui la contraint : on saisit une adresse, pas un
                      identifiant, et le `datalist` propose celles des personnes
                      actives. Le refus de grade (422) revient avec les deux
                      grades en cause et s'affiche tel quel, en haut de l'onglet. */}
                  <input
                    className="saisie"
                    list="emails-responsables"
                    defaultValue={emailDe(p.responsableId)}
                    placeholder="manager@elyadata.com"
                    onBlur={(e) => void definitResponsable(p, e.target.value)}
                    aria-label={`Manager of ${p.nom}`}
                  />
                </td>
                <td>
                  {/* Lecture seule ici : la fonction se pose par la barre d'affectation
                      en masse ci-dessus, ou par l'import de l'organigramme. Le libellé
                      vient de l'API (`libelleFonction` d'`api/src/domain/fonctions.ts`) :
                      l'écran ne recalcule pas le vocabulaire du domaine. */}
                  <span className="barre-actions">
                    {[...new Set(p.appartenances.map((a) => a.libelleFonction))].map((f) => (
                      <Badge key={f}>{f}</Badge>
                    ))}
                  </span>
                </td>
                <td>
                  <select
                    className="selection"
                    style={{ width: 'auto' }}
                    value={p.role}
                    onChange={(e) => void modifie(p.id, { role: e.target.value as Role })}
                    aria-label={`Access level of ${p.nom}`}
                  >
                    {ROLES.map((role) => (
                      <option key={role} value={role}>
                        {libelleRole(role)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <button
                    type="button"
                    className="bouton bouton--petit"
                    onClick={() => void modifie(p.id, { actif: !p.actif })}
                  >
                    {p.actif ? 'Deactivate' : 'Reactivate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <datalist id="intitules">
        {[...new Set((personnes.donnees?.personnes ?? []).map((x) => x.intitulePoste).filter(Boolean))].map(
          (titre) => (
            <option key={titre} value={titre!} />
          ),
        )}
      </datalist>
      {/* Les adresses proposées pour le responsable : les personnes actives
          seulement — désigner une personne désactivée comme responsable serait
          une ligne hiérarchique qui ne mène nulle part. */}
      <datalist id="emails-responsables">
        {listePersonnes
          .filter((x) => x.actif !== false)
          .map((x) => (
            <option key={x.id} value={x.email} />
          ))}
      </datalist>

      {modalImport ? (
        <ModalImportOrganigramme
          fermer={() => setModalImport(false)}
          appliquee={() => {
            setModalImport(false);
            personnes.recharge();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * L'organigramme entier, collé en CSV. L'aperçu n'est pas dans le brief
 * d'origine : celui-ci décrit un import qui écrit dès le premier appel, et
 * qui ferme toute appartenance ouverte absente du fichier. Un export partiel
 * ou une colonne mal collée effacerait alors l'organisation entière sans
 * qu'on l'ait voulu — c'est pourquoi `appliquer` est ici toujours envoyé
 * explicitement : `false` d'abord pour voir le décompte des fermetures,
 * `true` ensuite, une fois qu'il ne surprend pas.
 *
 * La boîte de dialogue reprend les mêmes sémantiques que `ModalNouvelleEquipe`
 * (Task 9) : rôle, `aria-modal`, fermeture sur Échap, focus posé à l'ouverture
 * et rendu au déclencheur à la fermeture.
 */
function ModalImportOrganigramme({
  fermer,
  appliquee,
}: {
  fermer: () => void;
  appliquee: () => void;
}): JSX.Element {
  const [csv, setCsv] = useState('');
  const [resultat, setResultat] = useState<ResultatImportOrganisation | null>(null);
  const [anomalies, setAnomalies] = useState<AnomalieCsv[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const conteneur = useRef<HTMLDivElement>(null);
  const premierChamp = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    premierChamp.current?.focus();
  }, []);

  useEffect(() => {
    function surTouche(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        fermer();
        return;
      }
      if (e.key !== 'Tab' || !conteneur.current) return;

      const focusables = conteneur.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
      );
      if (focusables.length === 0) return;
      const premier = focusables[0]!;
      const dernier = focusables[focusables.length - 1]!;

      if (e.shiftKey && document.activeElement === premier) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && document.activeElement === dernier) {
        e.preventDefault();
        premier.focus();
      }
    }
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [fermer]);

  async function lance(appliquer: boolean): Promise<void> {
    if (enCours || csv.trim().length === 0) return;
    setEnCours(true);
    setErreur(null);
    try {
      const r = await appelApi<ResultatImportOrganisation>('/api/admin/organisation/importer', {
        methode: 'POST',
        corps: { csv, appliquer },
      });
      setResultat(r);
      setAnomalies([]);
      if (appliquer) appliquee();
    } catch (cause) {
      setResultat(null);
      if (cause instanceof ErreurApi) {
        const brutes = (cause.details as { anomalies?: unknown } | undefined)?.anomalies;
        setAnomalies(Array.isArray(brutes) ? (brutes as AnomalieCsv[]) : []);
        setErreur(cause.message);
      } else {
        setAnomalies([]);
        setErreur('Import failed');
      }
    } finally {
      setEnCours(false);
    }
  }

  // Rien à écrire tant qu'un aperçu propre (sans anomalie) n'a pas été vu :
  // appliquer à l'aveugle est exactement ce que l'aperçu doit empêcher.
  const peutApercevoir = csv.trim().length > 0 && !enCours;
  const peutAppliquer = peutApercevoir && resultat !== null && !resultat.applique && anomalies.length === 0;

  return (
    <div
      className="modal-fond"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) fermer();
      }}
    >
      <div
        ref={conteneur}
        className="apercu-teams"
        style={{ marginTop: 0, width: '100%', maxWidth: 640 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titre-modal-import"
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <h3 id="titre-modal-import">Import org chart</h3>
          <button type="button" className="bouton bouton--fantome bouton--petit" onClick={fermer}>
            Cancel
          </button>
        </div>

        <p className="discret" style={{ marginTop: 8 }}>
          Paste the whole chart as CSV, header included:{' '}
          <code>email,groupe,fonction,grade</code>. An address unknown to the base is an anomaly, not
          a new person — someone only exists after their first sign-in. A team membership open in the
          base but absent from the file is closed.
        </p>
        {/* La limite est acceptable, la surprise ne l'est pas : `analyseCsvOrganisation`
            découpe sur les virgules sans gérer les guillemets. */}
        <p className="discret" style={{ marginTop: 8 }}>
          Lines are split on commas, with no quote handling: a team named “Data, IA” cannot be
          imported — rename it, or assign it from the table above.
        </p>

        <label className="champ" style={{ marginTop: 16 }}>
          <span className="champ__libelle">CSV</span>
          <textarea
            ref={premierChamp}
            className="zone-texte"
            rows={10}
            style={{ fontFamily: 'var(--font-mono, monospace)' }}
            value={csv}
            onChange={(e) => {
              setCsv(e.target.value);
              setResultat(null);
              setAnomalies([]);
              setErreur(null);
            }}
            placeholder={'email,groupe,fonction,grade\ndev@elyadata.com,Data,Chapter Lead,7'}
            spellCheck={false}
            aria-label="CSV"
          />
        </label>

        <div className="barre-actions" style={{ marginTop: 16 }}>
          <button
            type="button"
            className="bouton"
            disabled={!peutApercevoir}
            onClick={() => void lance(false)}
          >
            Preview
          </button>
          <button
            type="button"
            className="bouton bouton--primaire"
            disabled={!peutAppliquer}
            title={
              peutAppliquer
                ? undefined
                : 'Preview the file first — nothing is written before you have seen the count of closures.'
            }
            onClick={() => void lance(true)}
          >
            Apply
          </button>
        </div>

        {erreur ? (
          <div style={{ marginTop: 16 }}>
            <Message variante="erreur">{erreur}</Message>
            {anomalies.length > 0 ? (
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {anomalies.slice(0, 10).map((a, i) => (
                  <li key={i} className="discret">
                    Line {a.ligne}: {a.message}
                  </li>
                ))}
                {anomalies.length > 10 ? (
                  <li className="discret">…and {anomalies.length - 10} more.</li>
                ) : null}
              </ul>
            ) : null}
          </div>
        ) : null}

        {resultat ? (
          <div style={{ marginTop: 16 }}>
            <Message variante={resultat.applique ? 'succes' : 'info'}>
              {/* `personnes` compte les adresses distinctes du fichier. Il est
                  montré parce qu'il est le seul chiffre qui dise si le fichier
                  couvre l'organisation entière : c'est à lui qu'on voit un
                  export tronqué, avant même de lire le décompte des
                  fermetures. */}
              {resultat.applique
                ? `Applied for ${resultat.personnes} address(es): ${resultat.ouvertes} opened, ${resultat.fermees} closed, ${resultat.fonctions} function(s) reassigned, ${resultat.grades} grade(s) set.`
                : `Preview only for ${resultat.personnes} address(es), nothing written: ${resultat.ouvertes} would open, ${resultat.fermees} would close, ${resultat.fonctions} function(s) would be reassigned, ${resultat.grades} grade(s) would be set.`}
            </Message>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Journal
// ---------------------------------------------------------------------------

function Journal(): JSX.Element {
  const journal = useRequete<{ entrees: EntreeJournal[] }>('/api/admin/journal?limite=200');

  return (
    <>
      <p className="discret" style={{ marginBottom: 20 }}>
        Every administrative action is logged: who, what, when, and the detail.
      </p>

      {journal.enCours && !journal.donnees ? <Chargement /> : null}

      <div className="tableau-defilant">
        <table className="tableau">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Author</th>
              <th>Action</th>
              <th>Target</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {(journal.donnees?.entrees ?? []).map((e) => (
              <tr key={e.id}>
                <td className="discret" style={{ whiteSpace: 'nowrap' }}>
                  {dateLongue(e.horodatage)}
                </td>
                <td>{e.auteur}</td>
                <td className="mono">{e.action}</td>
                <td className="mono discret">{e.cible}</td>
                <td className="discret" style={{ maxWidth: 420, wordBreak: 'break-word' }}>
                  {e.details ? JSON.stringify(e.details) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
